/**
 * The one-click status strip's words, in one place: the strip renders them
 * and the live-trading log (#1025) records them, so the two never disagree.
 * Moved verbatim from OneClickArmControl; `oneClickAckText.cases.json` pins
 * every pre-move output.
 */
import {
  CHECK_OPEN_ORDERS,
  type OneClickAck,
  type OneClickLegAck,
} from '@/stores/oneClickStore';
import { fillText } from '@/stores/oneClickFillWatch';

export function ackTone(status: OneClickAck['status'] | OneClickLegAck['status']): string {
  switch (status) {
    case 'accepted':
      return 'text-positive';
    case 'rejected':
      return 'text-negative';
    // #724 — a partial two-sided press is a "go look" state, like unverified:
    // one leg rests alone and the trader has to decide what to do with it.
    case 'throttled':
    case 'unverified':
    case 'partial':
      return 'text-amber-400';
    default:
      return 'text-muted-foreground';
  }
}

/** The press's own legs (#724), without any earlier press's line (D13). */
export function ownLegs(ack: OneClickAck): OneClickLegAck[] {
  return (ack.legs ?? []).filter((leg) => !leg.earlier);
}

export function ackText(ack: OneClickAck): string {
  switch (ack.status) {
    case 'pending':
      // A pending line that carries a message is a lifecycle line (D2: "Pull
      // All — cancelling resting Kalshi orders on DAL @ GSV…"), not an order
      // being placed.
      return ack.message ? `${ack.label} — ${ack.message}` : `Placing ${ack.label}…`;
    case 'accepted':
      // #1025: what this press actually filled leads the line once its fill
      // rows arrive; "sent" marks the label's price as the order's own, so a
      // wrapped "@ 61¢" never reads as the fill (Nadya, 10/20 look call).
      if (ack.fill != null) {
        return `${fillText(ack.fill)} — sent ${ack.label}${ack.message ? ` — ${ack.message}` : ''}`;
      }
      // A message on an accepted ack is its receipt (Pull All's "3/3 cancels
      // sent", #943) — the trader's only record, so show it.
      return ack.message ? `${ack.label} — ${ack.message}` : `Placed ${ack.label}`;
    case 'rejected':
      return `Rejected ${ack.label}${ack.message ? ` — ${ack.message}` : ''}`;
    case 'throttled':
      return ack.message ?? 'Click ignored — pacing';
    case 'unverified':
      return `${ack.label} — ${ack.message ?? `accepted but not on the book; ${CHECK_OPEN_ORDERS}`}`;
    case 'partial':
      // Only a two-sided press (legs of its own) placed anything partially.
      // A partial Pull All (D2) placed nothing: its message names what was
      // not cancelled.
      return ownLegs(ack).length > 0
        ? `Partially placed ${ack.label}${ack.message ? ` — ${ack.message}` : ''}`
        : `${ack.label}${ack.message ? ` — ${ack.message}` : ''}`;
  }
}

export const LEG_GLYPH: Record<OneClickLegAck['status'], string> = {
  pending: '…',
  accepted: '✓',
  rejected: '✗',
  unverified: '?',
};

/** #724 — one leg of a two-sided press, as the strip states it. */
export function legText(leg: OneClickLegAck): string {
  if (leg.earlier) return `${leg.label}${leg.message ? ` — ${leg.message}` : ''}`;
  return `${LEG_GLYPH[leg.status]} ${leg.label}${leg.message ? ` — ${leg.message}` : ''}`;
}
